import React, { useCallback, useEffect, useState } from "react";
import { Modal, BlockStack, Text, Select, TextField, InlineStack } from "@shopify/polaris";
import { GetApiCall, ApiCall } from "../../helper/axios";

/* Star rating control (SVG, no extra deps). */
function StarRating({ value, onSelect }) {
    const [hover, setHover] = useState(0);
    const active = hover || value;
    return (
        <InlineStack gap="200" align="center">
            {[1, 2, 3, 4, 5].map((n) => (
                <span
                    key={n}
                    role="button"
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onSelect(n)}
                    style={{ cursor: "pointer", lineHeight: 0 }}
                >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill={n <= active ? "#ffb800" : "none"} stroke="#ffb800" strokeWidth="1.5">
                        <path d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.77l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.94L12 2.5z" />
                    </svg>
                </span>
            ))}
        </InlineStack>
    );
}

const CATEGORY_FALLBACK = [
    "Bug Report", "Missing Feature", "Performance Issue", "Design Issue", "Support Issue", "Other",
];

/*
 * In-app review request. Self-contained: checks eligibility on mount and only
 * renders if the server says the merchant qualifies (anti-spam + usage signals
 * are enforced server-side). 5 stars -> App Store redirect; <5 stars -> private
 * feedback capture.
 */
function ReviewPromptModal({ token, openSignal = 0, presetRating = 0, onStateChange, disableAuto = false }) {
    const [open, setOpen] = useState(false);
    const [openedManually, setOpenedManually] = useState(false);
    const [step, setStep] = useState("rating"); // rating | thanks5 | feedback | done
    const [rating, setRating] = useState(0);
    const [appStoreUrl, setAppStoreUrl] = useState("https://apps.shopify.com/currency-converter#modal-show=WriteReviewModal");
    const [categories, setCategories] = useState(CATEGORY_FALLBACK);
    const [category, setCategory] = useState(CATEGORY_FALLBACK[0]);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const sendEvent = useCallback(
        (action, extra = {}) => {
            if (!token) return;
            ApiCall("POST", "/review/event", { action, ...extra }, { authentication: token }).catch(() => {});
        },
        [token]
    );

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            if (!token) return;
            try {
                const res = await GetApiCall("GET", "/review/eligibility", { authentication: token });
                const data = res?.data?.data;
                if (!cancelled && res?.data?.statusCode === 200 && data) {
                    // Always capture the configured App Store URL + categories so a
                    // manual open (from the Analytics tab) uses the real values too.
                    if (data.appStoreUrl) setAppStoreUrl(data.appStoreUrl);
                    if (Array.isArray(data.categories) && data.categories.length) {
                        setCategories(data.categories);
                        setCategory(data.categories[0]);
                    }
                    if (data.show && !disableAuto) {
                        setOpenedManually(false);
                        setOpen(true);
                        sendEvent("shown");
                    }
                }
            } catch (e) {
                /* never block the app on the review check */
            }
        };
        check();
        return () => { cancelled = true; };
    }, [token, sendEvent, disableAuto]);

    // Manual open from a persistent entry point (e.g. the "Leave a review" card
    // on the Analytics tab). Bypasses the eligibility gate and is NOT counted as
    // an auto-prompt impression, so it never trips the anti-spam rules.
    useEffect(() => {
        if (openSignal > 0) {
            setComment("");
            setOpenedManually(true);
            // The banner can pre-select a rating: 1-4 jumps straight to the
            // feedback form; otherwise start at the star-rating step.
            if (presetRating >= 1 && presetRating <= 4) {
                setRating(presetRating);
                setStep("feedback");
            } else if (presetRating === 5) {
                setRating(5);
                setStep("thanks5");
            } else {
                setRating(0);
                setStep("rating");
            }
            setOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSignal]);

    const handleClose = () => {
        // Closing at the rating step (without rating) counts as a dismissal and
        // suppresses the AUTO prompt permanently (anti-spam). A manually opened
        // prompt closing should not suppress anything.
        if (!openedManually && step === "rating" && rating === 0) sendEvent("dismissed");
        setOpen(false);
    };

    const handleSelectRating = (n) => {
        setRating(n);
        sendEvent("rated", { rating: n });
        setStep(n === 5 ? "thanks5" : "feedback");
    };

    const handleLeaveReview = () => {
        sendEvent("redirected");
        try { window.open(appStoreUrl, "_blank", "noopener,noreferrer"); } catch (e) { /* ignore */ }
        setOpen(false);
        if (onStateChange) onStateChange();
    };

    const handleSubmitFeedback = async () => {
        setSubmitting(true);
        try {
            await ApiCall("POST", "/review/feedback", { rating, category, comment }, { authentication: token });
        } catch (e) {
            /* swallow */
        } finally {
            setSubmitting(false);
            setStep("done");
            if (onStateChange) onStateChange(); // let the banner re-check its visibility
        }
    };

    if (!open) return null;

    let title = "How's it going?";
    let primaryAction = null;
    let secondaryActions = [];
    let body = null;

    if (step === "rating") {
        body = (
            <BlockStack gap="300" inlineAlign="center">
                <Text as="p" variant="bodyMd" alignment="center">
                    Enjoying the currency converter? Let us know how we're doing.
                </Text>
                <StarRating value={rating} onSelect={handleSelectRating} />
            </BlockStack>
        );
        secondaryActions = [{ content: "Maybe later", onAction: handleClose }];
    } else if (step === "thanks5") {
        title = "Thank you!";
        body = (
            <BlockStack gap="200">
                <Text as="p" variant="bodyMd">
                    Thank you! Would you mind leaving us a review on the Shopify App Store?
                    It really helps us out.
                </Text>
            </BlockStack>
        );
        primaryAction = { content: "Leave review", onAction: handleLeaveReview };
        secondaryActions = [{ content: "Not now", onAction: () => setOpen(false) }];
    } else if (step === "feedback") {
        title = "We'd love to hear how we can improve";
        body = (
            <BlockStack gap="300">
                <Text as="p" variant="bodyMd">
                    Thanks for the honest rating. Tell us what we can do better and we'll
                    take it on board.
                </Text>
                <Select
                    label="Category"
                    options={categories.map((c) => ({ label: c, value: c }))}
                    value={category}
                    onChange={setCategory}
                />
                <TextField
                    label="What can we improve?"
                    value={comment}
                    onChange={setComment}
                    multiline={4}
                    autoComplete="off"
                    placeholder="Share as much detail as you like..."
                />
            </BlockStack>
        );
        primaryAction = { content: "Send feedback", onAction: handleSubmitFeedback, loading: submitting };
        secondaryActions = [{ content: "Cancel", onAction: () => setOpen(false) }];
    } else if (step === "done") {
        title = "Thanks for your feedback!";
        body = (
            <Text as="p" variant="bodyMd">
                We've logged your feedback and our team will review it. Thank you for
                helping us improve.
            </Text>
        );
        primaryAction = { content: "Close", onAction: () => setOpen(false) };
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={title}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
        >
            <Modal.Section>{body}</Modal.Section>
        </Modal>
    );
}

export default ReviewPromptModal;
